<template>
  <v-card flat color="transparent">
    <v-card-title class="pb-1">
      <div>
        <div class="text-h6 font-weight-bold">Nuevo cliente</div>
        <div class="caption grey--text">
          Con el nombre alcanza. El resto se completa después.
        </div>
      </div>
    </v-card-title>

    <v-card-text class="pt-4">
      <!-- Quién es: cambia qué se pide abajo -->
      <div class="caption grey--text mb-2">¿A quién le vendés?</div>
      <v-row dense class="mb-2">
        <v-col cols="6" v-for="op in tipos" :key="op.value">
          <v-card
            outlined
            class="tipo-card pa-3 text-center"
            :class="{ 'tipo-card--activa': esPersonaFisica === op.value }"
            @click="esPersonaFisica = op.value"
          >
            <v-icon :color="esPersonaFisica === op.value ? 'primary' : 'grey'">
              {{ op.icon }}
            </v-icon>
            <div class="body-2 font-weight-medium mt-1">{{ op.text }}</div>
            <div class="caption grey--text">{{ op.hint }}</div>
          </v-card>
        </v-col>
      </v-row>

      <v-form ref="form" v-model="formValido" @submit.prevent="registrar">
        <!-- Identidad -->
        <template v-if="esPersonaFisica">
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.nombre"
                label="Nombre *"
                outlined
                dense
                autofocus
                :rules="[reglaRequerido]"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="form.apellido" label="Apellido" outlined dense />
            </v-col>
          </v-row>
        </template>
        <template v-else>
          <v-text-field
            v-model="form.razonSocial"
            label="Razón social *"
            placeholder="Distribuidora El Roble S.R.L."
            outlined
            dense
            autofocus
            :rules="[reglaRequerido]"
          />
        </template>

        <!-- Contacto -->
        <div class="caption grey--text mt-2 mb-2">Contacto</div>
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.telefono"
              label="Teléfono"
              outlined
              dense
              hide-details="auto"
              :rules="[reglaTelefono]"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.email"
              label="Email"
              outlined
              dense
              hide-details="auto"
              :rules="[reglaEmail]"
            />
          </v-col>
        </v-row>

        <!-- Lo fiscal sólo estorba hasta que hay que facturar -->
        <div class="mt-4">
          <v-btn text small color="primary" class="ml-n2" @click="mostrarFiscal = !mostrarFiscal">
            <v-icon left small>{{ mostrarFiscal ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            Datos para facturar
            <span v-if="!mostrarFiscal" class="grey--text caption ml-2">(opcional)</span>
          </v-btn>

          <v-expand-transition>
            <div v-show="mostrarFiscal" class="mt-2">
              <v-row dense>
                <v-col cols="12" sm="5">
                  <v-select
                    v-model="form.tipo_documento"
                    :items="tiposDocumento"
                    item-text="description"
                    item-value="id"
                    label="Tipo"
                    outlined
                    dense
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="7">
                  <v-text-field
                    v-model="form.documento"
                    label="Número"
                    outlined
                    dense
                    hide-details="auto"
                    :rules="[reglaDocumento]"
                  />
                </v-col>
                <v-col cols="12">
                  <v-select
                    v-model="form.condicionIva"
                    :items="condicionesIva"
                    item-text="nombre"
                    item-value="id"
                    label="Condición frente al IVA"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
              </v-row>
              <div class="caption grey--text mt-2">
                Sin CUIT y condición de IVA sólo se le puede emitir comprobante a
                consumidor final.
              </div>
            </div>
          </v-expand-transition>
        </div>
      </v-form>
    </v-card-text>

    <v-card-actions class="px-6 pb-4">
      <v-spacer />
      <v-btn text @click="cerrar">Cancelar</v-btn>
      <v-btn color="primary" depressed :loading="guardando" @click="registrar">
        <v-icon left small>mdi-check</v-icon>
        Registrar cliente
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';

const FORM_VACIO = {
  nombre: '',
  apellido: '',
  razonSocial: '',
  telefono: '',
  email: '',
  documento: '',
  tipo_documento: null,
  condicionIva: null,
};

export default {
  name: 'ClienteCreate',
  data() {
    return {
      esPersonaFisica: true,
      mostrarFiscal: false,
      formValido: true,
      guardando: false,
      tiposDocumento: [],
      condicionesIva: [],
      form: { ...FORM_VACIO },
      tipos: [
        { value: true, text: 'Persona', icon: 'mdi-account', hint: 'Nombre y apellido' },
        { value: false, text: 'Empresa', icon: 'mdi-domain', hint: 'Razón social' },
      ],
    };
  },

  watch: {
    // Cambiar de tipo limpia lo del otro: si no, una empresa se guarda con el
    // nombre de persona que quedó tipeado.
    esPersonaFisica(esFisica) {
      if (esFisica) {
        this.form.razonSocial = '';
      } else {
        this.form.nombre = '';
        this.form.apellido = '';
      }
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
  },

  methods: {
    ...mapActions('cliente', ['registrar_cliente_rapido']),

    /**
     * Este formulario se usa de dos formas: dentro del diálogo del listado y
     * como pantalla propia en /clientes/nuevo. En el diálogo lo cierra el
     * padre; como pantalla no hay padre que escuche, así que vuelve al listado.
     */
    cerrar() {
      if (this.$listeners['close-dialog']) {
        this.$emit('close-dialog');
        return;
      }
      const esPos = this.$route.name?.includes('pos') || this.$route.path?.includes('/pos');
      this.$router.push({ name: esPos ? 'pos.clientes' : 'empresa.clientes' });
    },

    reglaRequerido(v) {
      return !!(v || '').trim() || 'Requerido';
    },

    reglaEmail(v) {
      if (!v) return true;
      return /.+@.+\..+/.test(v) || 'Email inválido';
    },

    reglaTelefono(v) {
      if (!v) return true;
      return /^[\d\s+()-]{6,}$/.test(v) || 'Teléfono inválido';
    },

    reglaDocumento(v) {
      if (!v) return true;
      return /^\d{7,11}$/.test(v) || 'Entre 7 y 11 dígitos, sin puntos ni guiones';
    },

    async cargarOpciones() {
      try {
        const [tipos, condiciones] = await Promise.all([
          this.axios.get('/clientes/tipodocumento'),
          this.axios.get('/clientes/condicioniva'),
        ]);
        this.tiposDocumento = tipos.data;
        this.condicionesIva = condiciones.data;
      } catch (e) {
        console.error(e);
      }
    },

    async registrar() {
      if (!this.$refs.form.validate()) return;

      this.guardando = true;
      try {
        const payload = {
          es_persona_fisica: this.esPersonaFisica,
          nombre: this.esPersonaFisica ? this.form.nombre.trim() : null,
          apellido: this.esPersonaFisica ? this.form.apellido.trim() || null : null,
          razonSocial: this.esPersonaFisica ? null : this.form.razonSocial.trim(),
          telefono: this.form.telefono.trim() || null,
          email: this.form.email.trim() || null,
          documento: this.form.documento.trim() || null,
          tipo_documento: this.form.tipo_documento,
          condicionIva: this.form.condicionIva,
        };

        const cliente = await this.registrar_cliente_rapido(payload);

        this.$updateBus.$emit('showSnackBar', {
          show: true, button: false, color: 'success',
          text: 'Cliente registrado', icon: 'mdi-check', timeout: 2500,
        });
        this.form = { ...FORM_VACIO };
        this.$refs.form.resetValidation();
        this.$emit('cliente-creado', cliente);
        this.cerrar();
      } catch (e) {
        const data = e.response?.data || {};
        const detalle = data.detail
          || Object.values(data).flat()[0]
          || 'No se pudo registrar el cliente';
        this.$updateBus.$emit('showSnackBar', {
          show: true, button: false, color: 'error',
          text: detalle, icon: 'mdi-alert', timeout: 4000,
        });
      } finally {
        this.guardando = false;
      }
    },
  },

  created() {
    this.cargarOpciones();
  },
};
</script>

<style scoped>
.tipo-card {
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
}

.tipo-card:hover {
  background-color: rgba(255, 255, 255, 0.04);
}

.tipo-card--activa {
  border-color: var(--v-primary-base) !important;
  background-color: rgba(33, 150, 243, 0.08);
}
</style>
